import type {
  ReportEntityType,
  ReportTemplate,
  ReportTemplateDraft,
  ReportTemplateField,
  StoredReportQuery
} from 'v5/api/report-templates/reportTemplates.api';
import type { FieldNameTypeMapping } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import {
  reportEditorStateOf,
  storedQueryOf,
  type ReportEditorState
} from 'v5/core/reporting/reportTemplateQuery';
import {
  DEFAULT_REPORT_RELATIVE_DATE,
  reportDateFieldsOf
} from './reportDateFilter';

export type ReportTemplateFormValues = {
  name: string;
  description: string;
  entityType: ReportEntityType;
  /** The type select's value; `null` is every type. */
  typeId: string | null;
  query: ReportEditorState;
  fields: ReportTemplateField[];
};

const EMPTY_REPORT_QUERY: StoredReportQuery = {
  filters: {},
  date_filters: []
};

/** A new template: Resources created in the last 30 days, no columns yet. */
export function newReportTemplateFormValues(): ReportTemplateFormValues {
  return {
    name: '',
    description: '',
    entityType: 'contact',
    typeId: null,
    query: {
      ...reportEditorStateOf('contact', EMPTY_REPORT_QUERY),
      date: {
        field: 'created_at',
        kind: 'relative',
        relative: DEFAULT_REPORT_RELATIVE_DATE
      }
    },
    fields: []
  };
}

export function reportTemplateFormValuesOf(
  template: ReportTemplate
): ReportTemplateFormValues {
  const query = reportEditorStateOf(template.entityType, template.query);
  return {
    name: template.name,
    description: template.description ?? '',
    entityType: template.entityType,
    typeId: query.typeId === null ? null : String(query.typeId),
    query,
    fields: template.fields
  };
}

/**
 * "Report on" changed: the type, the conditions and the columns belong to the old entity and go;
 * the date stays, on the creation date when the new entity has no such field.
 */
export function withReportEntityType(
  values: ReportTemplateFormValues,
  entityType: ReportEntityType
): ReportTemplateFormValues {
  const { date } = values.query;
  return {
    ...values,
    entityType,
    typeId: null,
    query: {
      ...reportEditorStateOf(entityType, EMPTY_REPORT_QUERY),
      date:
        date && !reportDateFieldsOf(entityType).includes(date.field)
          ? { ...date, field: 'created_at' }
          : date
    },
    fields: []
  };
}

/**
 * What the form saves. The stored query is the template's own when it still reports on the same
 * entity, so untouched conditions keep their stored shape; after "Report on" changed it starts
 * empty. Throws when a changed condition has no stored shape (`storedQueryOf`).
 */
export function reportTemplateDraftOf(
  values: ReportTemplateFormValues,
  template: ReportTemplate | null,
  fieldTypes: FieldNameTypeMapping
): ReportTemplateDraft {
  const original =
    template && template.entityType === values.entityType
      ? template.query
      : EMPTY_REPORT_QUERY;
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    entityType: values.entityType,
    fields: values.fields,
    query: storedQueryOf(
      values.entityType,
      original,
      {
        ...values.query,
        typeId: values.typeId === null ? null : Number(values.typeId)
      },
      fieldTypes
    )
  };
}
